import { i18n } from '@lingui/core';
import { msg, plural, t } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { resolvePatternText } from '@/shared/patterns/text';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { communicationSettingFilters } from './communication-setting.filters';

// Manifesto da Régua de comunicação (U23, U42). Arquivo leve: entra no `src/app/screens.ts` e no ⌘K;
// o fio da exclusão vem por `import()` na hora de rodar.

export const COMMUNICATION_SETTING_PATH = '/gestao-carteira/regua';
/** Id do registro novo no `?edit=` (o "nova" do legado, `NEW_RECORD_ID` de `shared/form`). */
export const NEW_COMMUNICATION_SETTING = 'novo';

// O legado não tem gate por ação: tudo herda o `BillingAccount:Read` de `/gestao-carteira` (`router.tsx:1341-1347`).
const READ = can('BillingAccount', 'Read');
const idInput = z.object({ id: z.string().min(1).max(64) });

const loadApi = () => import('./communication-setting.api');

export const communicationSettingCreate = action({
  id: 'communicationSetting.create',
  kind: 'draft',
  label: msg`Nova régua de comunicação`,
  description: msg`Abre o formulário em branco de régua de comunicação: convênio, nome, dias antes do vencimento para enviar a cobrança, número da carteira e os avisos ligados. Nada é gravado até a pessoa cadastrar.`,
  keywords: [msg`régua`, msg`comunicação`, msg`notificação`],
  shortcut: 'n',
  permission: READ,
  run: ({ nav }) => nav.search({ edit: NEW_COMMUNICATION_SETTING }),
});

export const communicationSettingEdit = action({
  id: 'communicationSetting.edit',
  kind: 'draft',
  row: true,
  page: true,
  label: msg`Editar`,
  description: msg`Abre o formulário de uma régua de comunicação pelo id, para conferir ou editar. Nada é gravado até a pessoa salvar.`,
  permission: READ,
  input: idInput,
  run: ({ nav, input }) => nav.search({ edit: input.id }),
});

export const communicationSettingDelete = action({
  id: 'communicationSetting.delete',
  kind: 'destructive',
  row: true,
  page: true,
  label: msg`Excluir`,
  description: msg`Exclui de vez uma régua de comunicação, pelo id. Não dá para desfazer. Pede a confirmação da pessoa.`,
  permission: READ,
  input: idInput.extend({ name: z.string().max(200).optional() }),
  // Texto do modal do legado (`CommunicationSettingContainer.tsx:174-175`).
  preview: (input) => {
    const name = input.name ?? '';
    return { title: t`Excluir ${name}`, detail: t`Tem certeza que deseja excluir essa régua?`, tone: 'destructive' };
  },
  run: async ({ input }) => {
    const api = await loadApi();
    await api.deleteCommunicationSetting(input.id);
    return input.id;
  },
});

/**
 * As cobranças da régua (`CommunicationSettingList.tsx:26-35`): a lista de Cobranças filtrada por
 * `communicationSettingId`, na mesma aba (U15). Caminho literal: o `billing` importa a porta deste módulo.
 */
export const communicationSettingOpenBillings = action({
  id: 'communicationSetting.openBillings',
  kind: 'navigate',
  row: true,
  page: true,
  label: msg`Ver cobranças`,
  description: msg`Abre, na mesma aba, a lista de cobranças filtrada por esta régua de comunicação. Não muda nada.`,
  permission: READ,
  input: idInput,
  run: ({ input, nav }) => nav.to('/gestao-carteira/cobrancas', { search: { communicationSettingId: input.id } }),
});

export const communicationSettingFilter = filterAction('communicationSetting.filter', communicationSettingFilters, {
  description: msg`Filtra a lista de réguas de comunicação: walletsCode (um ou mais códigos de convênio), name (nome), walletNumber (número da carteira) e daysToSendBillingBeforeDueDate (dias antes do vencimento). Filtro omitido é limpo.`,
});

export const communicationSettingListPage = listPageAction('communicationSetting.list.page');

export const communicationSettingListScreen = defineScreen({
  id: 'communicationSetting.list',
  path: COMMUNICATION_SETTING_PATH,
  title: msg`Régua de comunicação`,
  description: msg`Réguas de comunicação dos convênios (quantos dias antes do vencimento a cobrança é enviada e os avisos por e-mail, de atualização, de liquidação e de vencimento): filtros, paginação, criar, editar, excluir e ver as cobranças da régua.`,
  permission: READ,
  place: { icon: 'bell', section: 'wallet-management' },
  actions: [
    communicationSettingCreate,
    communicationSettingFilter,
    communicationSettingListPage,
    communicationSettingEdit,
    communicationSettingDelete,
    communicationSettingOpenBillings,
  ],
});

/** O que o agente lê da lista (U24, U46): contagem, filtros pelo rótulo e o sheet aberto, no idioma da tela. */
export function communicationSettingListContext(input: { readonly total: number | undefined; readonly filters: readonly string[]; readonly editing: string | undefined; readonly editingName?: string | undefined }): ScreenContext {
  const total = input.total;
  const count = total === undefined ? t`Carregando.` : plural(total, { one: '# régua na lista.', other: '# réguas na lista.' });
  const labels = input.filters.map((key) => {
    const label = communicationSettingFilters.fieldOf(key)?.label;
    return label ? resolvePatternText(i18n, label) : key;
  });
  const filterList = labels.join(', ');
  const filters = labels.length > 0 ? t`Filtros: ${filterList}.` : t`Sem filtros.`;
  const name = input.editingName ?? input.editing ?? '';
  const editing =
    input.editing === undefined ? '' : input.editing === NEW_COMMUNICATION_SETTING ? t`Formulário de nova régua aberto.` : t`Formulário da régua ${name} aberto.`;
  return {
    summary: [t`Lista de réguas de comunicação.`, count, filters, editing].filter(Boolean).join(' '),
    fields: { total: input.total ?? null, editing: input.editing ?? null },
    sensitivity: 'internal',
  };
}
